import { Archive, ArchiveRestore, EyeOff } from "lucide-react";
import type { WindowAction } from "../../web/components/RecordWindow";
import type { ItemState, PriceListData } from "./meta";

/**
 * Its More menu's states (states.ts): for sale, hidden (archived in ServiceM8, on standby) or retired, each asked first.
 * `inBundles`: the active bundles holding it (hiding or retiring one they hold: they'll need it taken out).
 */
export function stateActions(name: string, state: ItemState, inBundles: number, onState: (state: ItemState) => void, product = false): WindowAction[] {
    // A product (catalogue.ts) is kept only here: in use, or archived (items built from it keep it).
    if (product) {
        return state === "retired"
            ? [{ icon: <ArchiveRestore className="size-4" />, label: "Restore", help: "In use again.", confirm: { title: "Restore this product?", subject: name, message: "Items can be linked to it again.", confirmLabel: "Restore" }, run: () => onState("sale") }]
            : [
                  {
                      icon: <Archive className="size-4" />,
                      label: "Archive",
                      help: "No longer used. It can be restored.",
                      danger: true,
                      confirm: { title: "Archive this product?", subject: name, message: "Items linked to it keep it, at its price and cost. You can restore it any time.", confirmLabel: "Archive" },
                      run: () => onState("retired"),
                  },
              ];
    }
    const holding = inBundles ? ` ${inBundles.toLocaleString()} ${inBundles === 1 ? "bundle holds" : "bundles hold"} it: they'll need it taken out.` : "";
    const hide: WindowAction = {
        icon: <EyeOff className="size-4" />,
        label: "Hide",
        help: "Not offered on jobs for now, kept ready for when it's needed.",
        confirm: {
            title: "Hide this item?",
            subject: name,
            message: `It's archived in ServiceM8, so it isn't offered on jobs, but it stays here priced and costed. Unhide it any time.${holding}`,
            confirmLabel: "Hide",
        },
        run: () => onState("hidden"),
    };
    const retire: WindowAction = {
        icon: <Archive className="size-4" />,
        label: "Archive",
        help: "No longer used. It can be restored.",
        danger: true,
        confirm: { title: "Archive this item?", subject: name, message: `It's archived in ServiceM8 and here. You can restore it any time.${holding}`, confirmLabel: "Archive" },
        run: () => onState("retired"),
    };
    const sell: WindowAction = {
        icon: <ArchiveRestore className="size-4" />,
        label: state === "hidden" ? "Unhide" : "Restore",
        help: "Visible on jobs again.",
        confirm: { title: state === "hidden" ? "Unhide this item?" : "Restore this item?", subject: name, message: "It's restored in ServiceM8, so it can be picked on jobs.", confirmLabel: state === "hidden" ? "Unhide" : "Restore" },
        run: () => onState("sale"),
    };
    if (state === "sale") return [hide, retire];
    if (state === "hidden") return [sell, retire];
    return [sell, hide];
}

/** "CYL570-KIT", or (taken by an item or bundle) "CYL570-KIT2"...: an item number no one has. */
export function freeNumber(data: PriceListData, wanted: string) {
    const taken = new Set([...data.materials, ...data.bundles].map((r) => r.item_number.trim().toUpperCase()));
    for (let n = 1; ; n++) {
        const number = n === 1 ? wanted : `${wanted}${n}`;
        if (!taken.has(number.toUpperCase())) return number;
    }
}
